import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatches } from 'react-router';
import { observer } from 'mobx-react-lite';
import { User as UserIcon, Close as CloseIcon } from '@linktivity/link-icons';
import { Link } from '@linktivity/link-ui';
import MenuIcon from '@link/share/assets/icons/menu.svg?react';
import { useIsMobile } from '@link/share/hooks';
import { ga4AccountEntryState } from '@link/share/vendors';
import Cart from './Cart';
import { HeaderContext } from './context';
import Currency from './Currency';
import { HeaderProps } from './header.types';
import Locale from './Locale';
import SearchBox from './SearchBox';
import User from './User';
import styles from './header.module.css';

const LOGIN_LINK_STATE = ga4AccountEntryState('header');

const Header: FC<HeaderProps> = observer(
  ({
    logo,
    localeList,
    showCurrency = false,
    supportedCurrencies,
    supportCart = false,
    cartStore,
    currentCurrency,
    onCurrencyChange,
    searchConfig,
    logout,
    loggedIn,
    isPasswordAuth
  }) => {
    const { t, i18n } = useTranslation();
    const currentLocale = useMemo(() => i18n.language, [i18n.language]);
    const matches = useMatches();
    const isMobile = useIsMobile();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const disableLang = (
      matches[matches.length - 1]?.handle as { disableLang?: boolean }
    )?.disableLang;
    const defaultSearchConfig = {
      enabled: false,
      onSearch: () => {}
    };

    const finalSearchConfig = searchConfig || defaultSearchConfig;

    const featureCount = useMemo(
      () =>
        [!disableLang, supportCart && !!cartStore, showCurrency].filter(Boolean)
          .length + 1, // +1 for the always-present user/login item
      [disableLang, supportCart, cartStore, showCurrency]
    );

    const showMobileMenu = isMobile && featureCount > 4;

    useEffect(() => {
      if (!showMobileMenu) setIsMobileMenuOpen(false);
    }, [showMobileMenu]);

    const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

    useEffect(() => {
      if (!isMobileMenuOpen) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeMobileMenu();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMobileMenuOpen, closeMobileMenu]);

    useEffect(() => {
      if (!isMobileMenuOpen) return;

      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }, [isMobileMenuOpen]);

    const inlineNavItems = !showMobileMenu ? (
      <>
        {!disableLang && <Locale localeList={localeList} />}
        {supportCart && cartStore && (
          <Cart
            cartStore={cartStore}
            loggedIn={loggedIn}
            currentCurrency={currentCurrency}
          />
        )}
        {showCurrency && (
          <Currency
            currentCurrency={currentCurrency}
            supportedCurrencies={supportedCurrencies}
            onChange={onCurrencyChange}
          />
        )}
        {loggedIn ? (
          <User />
        ) : (
          <Link
            to={`/${currentLocale}/login`}
            state={LOGIN_LINK_STATE}
            className={styles.navButton}
          >
            <UserIcon className={styles.navIcon} />
            <span className={styles.text}>{t('share.login.button')}</span>
          </Link>
        )}
      </>
    ) : null;

    return (
      <HeaderContext.Provider
        value={{ logout, isPasswordAuth, closeMobileMenu }}
      >
        <header className={styles.header}>
          <div className={styles.inner}>
            <div className={styles.left}>
              <div className={styles.logo}>
                {logo && (
                  <Link to={`/${currentLocale}`} className={styles.logoInner}>
                    <img src={logo} alt="logo" className={styles.logoImage} />
                  </Link>
                )}
              </div>
            </div>
            {finalSearchConfig.enabled && (
              <div className={styles.center}>
                <SearchBox config={finalSearchConfig} />
              </div>
            )}
            <div className={styles.right}>
              {showMobileMenu ? (
                <button
                  className={styles.menuToggle}
                  type="button"
                  aria-label={
                    isMobileMenuOpen
                      ? t('share.header.closeMenu')
                      : t('share.header.openMenu')
                  }
                  aria-controls="mobile-menu-panel"
                  aria-expanded={isMobileMenuOpen}
                  onClick={() => setIsMobileMenuOpen(v => !v)}
                >
                  {isMobileMenuOpen ? (
                    <CloseIcon className={styles.navIcon} />
                  ) : (
                    <MenuIcon className={styles.navIcon} />
                  )}
                </button>
              ) : (
                <div className={styles.nav}>{inlineNavItems}</div>
              )}
            </div>
          </div>
          {showMobileMenu && isMobileMenuOpen && (
            <button
              type="button"
              className={styles.mobileMenuBackdrop}
              aria-label={t('share.header.closeMenu')}
              onClick={closeMobileMenu}
            />
          )}
          {showMobileMenu && isMobileMenuOpen && (
            <nav id="mobile-menu-panel" className={styles.mobileMenuPanel}>
              {!disableLang && (
                <div className={styles.mobileMenuItem}>
                  <Locale localeList={localeList} />
                </div>
              )}
              {supportCart && cartStore && (
                <div className={styles.mobileMenuItem}>
                  <Cart
                    cartStore={cartStore}
                    loggedIn={loggedIn}
                    currentCurrency={currentCurrency}
                  />
                </div>
              )}
              {showCurrency && (
                <div className={styles.mobileMenuItem}>
                  <Currency
                    currentCurrency={currentCurrency}
                    supportedCurrencies={supportedCurrencies}
                    onChange={onCurrencyChange}
                  />
                </div>
              )}
              <div className={styles.mobileMenuItem}>
                {loggedIn ? (
                  <User />
                ) : (
                  <Link
                    to={`/${currentLocale}/login`}
                    state={LOGIN_LINK_STATE}
                    className={styles.navButton}
                    onClick={closeMobileMenu}
                  >
                    <UserIcon className={styles.navIcon} />
                    <span className={styles.text}>
                      {t('share.login.button')}
                    </span>
                  </Link>
                )}
              </div>
            </nav>
          )}
        </header>
      </HeaderContext.Provider>
    );
  }
);

export default Header;
